/**
 * /admin/smartboxes — platform-only.
 *
 * Two panes:
 *   - "Pending SCUs" — SCUs publishing telemetry in the last 7
 *     days but not yet associated with a smartbox (either missing
 *     from gen5_computer entirely, or present with NULL
 *     aws_mysql_record_id). Click "Associate" to map to an
 *     existing SmartBox.
 *
 *   - "SmartBox roster" — every md_smartbox row with its
 *     currently-mapped SCU + last scan time. Lets the operator
 *     repoint an existing SmartBox at a different SCU after a Pi
 *     swap (or clear the link).
 *
 * Per slice D's docstring: creating brand-new SmartBoxes from
 * MTTR is a follow-on slice (D2) — md_smartbox lives in
 * `aws_smartbox3_mirror` which is read-only. For now operators
 * create the SmartBox in smartbox3, then come here to associate
 * the SCU.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtAgo, fmtUtc, fmtWorkCenter } from "../lib/format";
import { fetchCompanies, type Company } from "../services/adminApi";
import {
  type PendingScuRow,
  type SmartboxRosterRow,
  associateScu,
  createSmartbox,
  listPendingScus,
  listSmartboxRoster,
  setSmartboxManualConfig,
} from "../services/smartboxAdminApi";

export const AdminSmartboxesPage: React.FC = () => {
  const { token } = useAuth();
  const [pending, setPending] = useState<PendingScuRow[]>([]);
  const [roster, setRoster] = useState<SmartboxRosterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Modal: pick a smartbox for an SCU.
  const [picking, setPicking] = useState<{ scu: string; current_id: number | null } | null>(null);
  const [pickerQuery, setPickerQuery] = useState("");

  // Modal: create a new local SmartBox.
  const [creating, setCreating] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newCustomerId, setNewCustomerId] = useState<number | null>(null);
  const [newWorkcenter, setNewWorkcenter] = useState("");
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    if (!token || !creating || companies.length > 0) return;
    fetchCompanies(token)
      .then((cs) => setCompanies(cs.filter((c) => c.company_type === "customer")))
      .catch(() => setCompanies([]));
  }, [creating, token, companies.length]);

  const reload = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [p, r] = await Promise.all([
        listPendingScus(token, 7),
        listSmartboxRoster(token),
      ]);
      setPending(p.pending);
      setRoster(r.smartboxes);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onCreate = async (): Promise<void> => {
    if (!token || !newCode.trim() || newCustomerId == null) return;
    setBusy(true);
    setError(null);
    try {
      await createSmartbox(token, {
        code: newCode.trim(),
        name: newName.trim() || undefined,
        customer_company_id: newCustomerId,
        workcenter_name: newWorkcenter.trim() || undefined,
        note: newNote.trim() || undefined,
      });
      setCreating(false);
      setNewCode("");
      setNewName("");
      setNewCustomerId(null);
      setNewWorkcenter("");
      setNewNote("");
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onAssociate = async (scu: string, smartboxId: number | null): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await associateScu(token, scu, smartboxId);
      setPicking(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSetManual = async (
    smartboxId: number,
    manualCheckout: boolean,
    manualCheckin: boolean,
  ): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await setSmartboxManualConfig(token, smartboxId, manualCheckout, manualCheckin);
      setRoster((prev) =>
        prev.map((b) =>
          b.smartbox_id === smartboxId
            ? { ...b, manual_checkout_enabled: manualCheckout, manual_checkin_enabled: manualCheckin }
            : b,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const pickerHits = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return roster.slice(0, 25);
    return roster.filter((b) =>
      [b.smartbox_code, b.smartbox_name, b.work_center, b.customer_name]
        .some((v) => (v ?? "").toLowerCase().includes(q)),
    ).slice(0, 50);
  }, [roster, pickerQuery]);

  // Smart sort: smartboxes with no current SCU bubble up first
  // (they're the ones an operator most needs to look at). Then
  // group by customer for readability.
  const rosterSorted = useMemo(() => {
    const arr = [...roster];
    arr.sort((a, b) => {
      const aMapped = a.current_scu_name != null ? 1 : 0;
      const bMapped = b.current_scu_name != null ? 1 : 0;
      if (aMapped !== bMapped) return aMapped - bMapped;
      const ac = a.customer_name ?? "~";
      const bc = b.customer_name ?? "~";
      if (ac !== bc) return ac.localeCompare(bc);
      return (a.smartbox_code ?? "").localeCompare(b.smartbox_code ?? "");
    });
    return arr;
  }, [roster]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <header className="flex items-baseline justify-between gap-3">
        <h1 className="text-xl font-bold">SmartBox roster &amp; SCU associations</h1>
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={() => setCreating(true)} disabled={busy}>
            + New SmartBox
          </Button>
          <button
            type="button"
            onClick={() => void reload()}
            className="text-xs text-primary-600 hover:underline"
            disabled={loading || busy}
          >
            {loading ? "loading…" : "refresh"}
          </button>
        </div>
      </header>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Pending SCUs */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-700 mb-2">
          Pending SCUs
          <span className="ml-2 normal-case font-normal text-xs text-secondary-500">
            publishing in the last 7 days but not yet linked
          </span>
        </h2>
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-secondary-50 text-secondary-700 uppercase">
              <tr>
                <th className="text-left px-3 py-2">SCU</th>
                <th className="text-left px-3 py-2">Tailnet hostname</th>
                <th className="text-left px-3 py-2">HW</th>
                <th className="text-left px-3 py-2">Sources</th>
                <th className="text-left px-3 py-2">Last seen</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && pending.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-secondary-500">loading…</td></tr>
              ) : pending.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-success-700">
                  ✓ No pending SCUs — every actively-publishing device is linked.
                </td></tr>
              ) : (
                pending.map((p) => (
                  <tr key={p.scu_name} className="hover:bg-secondary-50">
                    <td className="px-3 py-2 font-mono">{p.scu_name}
                      {!p.in_gen5_computer && (
                        <span className="ml-2 text-[10px] uppercase bg-warning-100 text-warning-700 rounded px-1.5 py-0.5">
                          new
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-secondary-600">{p.tailscale_hostname ?? "—"}</td>
                    <td className="px-3 py-2">{p.hardware_type ?? "—"}</td>
                    <td className="px-3 py-2 text-secondary-500">{p.sources ?? "—"}</td>
                    <td className="px-3 py-2 text-secondary-600">
                      {p.last_seen_utc ? `${fmtAgo(p.last_seen_utc)} ago` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        onClick={() => setPicking({ scu: p.scu_name, current_id: null })}
                        disabled={busy}
                      >
                        Associate…
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* SmartBox roster */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-700 mb-2">
          SmartBoxes
          <span className="ml-2 normal-case font-normal text-xs text-secondary-500">
            {roster.length} total · unmapped first
          </span>
        </h2>
        <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-secondary-50 text-secondary-700 uppercase">
              <tr>
                <th className="text-left px-3 py-2">SmartBox</th>
                <th className="text-left px-3 py-2">Workcenter</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Current SCU</th>
                <th className="text-left px-3 py-2">Last scan</th>
                <th className="text-center px-3 py-2" title="Manual checkout enabled">Manual MC</th>
                <th className="text-center px-3 py-2" title="Manual checkin enabled">Manual MI</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {rosterSorted.map((b) => (
                <tr key={b.smartbox_id} className="hover:bg-secondary-50 align-top">
                  <td className="px-3 py-2 font-medium">
                    {b.smartbox_code ?? `#${b.smartbox_id}`}
                    {b.is_local && (
                      <span className="ml-1.5 text-[10px] uppercase bg-primary-100 text-primary-700 rounded px-1.5 py-0.5">
                        local
                      </span>
                    )}
                    {b.smartbox_name && b.smartbox_name !== b.smartbox_code && (
                      <div className="text-[10px] text-secondary-500">{b.smartbox_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-secondary-700">{fmtWorkCenter(b.work_center) || "—"}</td>
                  <td className="px-3 py-2 text-secondary-700">{b.customer_name ?? "—"}</td>
                  <td className="px-3 py-2 font-mono text-secondary-700">
                    {b.current_scu_name ?? (
                      <span className="text-warning-700 italic font-sans">unmapped</span>
                    )}
                    {b.tailscale_hostname && (
                      <div className="text-[10px] text-secondary-500">{b.tailscale_hostname}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-secondary-600">
                    {b.last_scan_utc ? `${fmtAgo(b.last_scan_utc)} ago` : "—"}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={b.manual_checkout_enabled}
                      disabled={busy}
                      title="Manual checkout enabled for this smartbox"
                      onChange={(e) =>
                        void onSetManual(b.smartbox_id, e.target.checked, b.manual_checkin_enabled)
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={b.manual_checkin_enabled}
                      disabled={busy}
                      title="Manual checkin enabled for this smartbox"
                      onChange={(e) =>
                        void onSetManual(b.smartbox_id, b.manual_checkout_enabled, e.target.checked)
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPicking({
                        scu: b.current_scu_name ?? "",
                        current_id: b.smartbox_id,
                      })}
                      disabled={busy || !b.current_scu_name}
                      title={!b.current_scu_name ? "No SCU mapped — associate one from the Pending list above" : "Re-associate or clear"}
                    >
                      Re-associate…
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Create-SmartBox modal */}
      {creating && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setCreating(false)}
        >
          <div
            className="bg-white rounded-md shadow-xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-semibold mb-1">Create local SmartBox</h2>
            <p className="text-xs text-secondary-500 mb-3">
              Use this when smartbox3 doesn't have the box yet. The new row gets
              a synthetic id ≥ 1,000,000; when smartbox3 catches up, re-associate
              the SCU to the canonical md_smartbox row and delete this one.
            </p>

            <div className="space-y-2">
              <label className="block text-xs text-secondary-600">
                <div>Code <span className="text-error-700">*</span></div>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder="e.g. SB101"
                  maxLength={64}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                  autoFocus
                />
              </label>
              <label className="block text-xs text-secondary-600">
                <div>Name (defaults to code)</div>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={128}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
              <label className="block text-xs text-secondary-600">
                <div>Customer <span className="text-error-700">*</span></div>
                <select
                  value={newCustomerId ?? ""}
                  onChange={(e) => setNewCustomerId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
                >
                  <option value="">— Pick a customer —</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-secondary-600">
                <div>Workcenter name</div>
                <input
                  type="text"
                  value={newWorkcenter}
                  onChange={(e) => setNewWorkcenter(e.target.value)}
                  placeholder="e.g. 00X07"
                  maxLength={64}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
              <label className="block text-xs text-secondary-600">
                <div>Note (optional)</div>
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  maxLength={512}
                  className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                />
              </label>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setCreating(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => void onCreate()}
                disabled={busy || !newCode.trim() || newCustomerId == null}
              >
                {busy ? "Creating…" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Picker modal */}
      {picking && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPicking(null)}
        >
          <div
            className="bg-white rounded-md shadow-xl max-w-2xl w-full p-5 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-base font-semibold mb-1">Associate SCU → SmartBox</h2>
            <div className="text-xs text-secondary-500 mb-3 font-mono">{picking.scu}</div>

            <input
              type="text"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search smartbox code / workcenter / customer…"
              className="w-full border border-secondary-300 rounded px-2 py-1 text-sm mb-2"
              autoFocus
            />

            <div className="border border-secondary-200 rounded-md max-h-80 overflow-y-auto">
              <table className="min-w-full text-xs">
                <tbody className="divide-y divide-secondary-100">
                  {pickerHits.map((b) => (
                    <tr
                      key={b.smartbox_id}
                      className={
                        "hover:bg-secondary-50 cursor-pointer " +
                        (picking.current_id === b.smartbox_id ? "bg-primary-50" : "")
                      }
                      onClick={() => void onAssociate(picking.scu, b.smartbox_id)}
                    >
                      <td className="px-3 py-1.5 font-semibold">
                        {b.smartbox_code ?? `#${b.smartbox_id}`}
                        {b.work_center && (
                          <span className="ml-1.5 font-normal text-secondary-600">
                            · {fmtWorkCenter(b.work_center)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-secondary-700">{b.customer_name ?? ""}</td>
                      <td className="px-3 py-1.5 text-secondary-500 font-mono">
                        {b.current_scu_name ? b.current_scu_name : "—"}
                      </td>
                    </tr>
                  ))}
                  {pickerHits.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-center text-secondary-500">
                        No matches.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <Button
                size="sm" variant="outline"
                onClick={() => void onAssociate(picking.scu, null)}
                disabled={busy}
              >
                Clear association
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPicking(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSmartboxesPage;
